import { useState } from "react";
import { Link } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { usePodcastUploads, type PodcastUploadJob } from "@src/context/PodcastUploadContext";
import ConfirmModal from "@src/components/modal/ConfirmModal";

function statusLabel(job: PodcastUploadJob): string {
  switch (job.status) {
    case "uploading":
      return `Uploading… ${job.progress}%`;
    case "creating-episode":
      return "Finishing up…";
    case "success":
      return "Uploaded";
    case "error":
      return job.error || "Upload failed";
    case "cancelled":
      return "Upload cancelled";
  }
}

/**
 * Route-independent progress card for background podcast uploads.
 * Mounted once next to <App /> so it stays visible on every page.
 */
export default function PodcastUploadStatusBar() {
  const { jobs, retryPodcastUpload, cancelPodcastUpload, dismissPodcastUpload } = usePodcastUploads();
  const [collapsed, setCollapsed] = useState(false);
  const [cancelJobId, setCancelJobId] = useState<string | null>(null);

  if (jobs.length === 0) return null;

  const cancelJob = jobs.find((j) => j.id === cancelJobId);

  const active = jobs.filter((j) => j.status === "uploading" || j.status === "creating-episode");
  const heading =
    active.length > 0
      ? `Uploading ${active.length} podcast episode${active.length > 1 ? "s" : ""}`
      : "Podcast uploads";

  return (
    <div
      className="fixed right-16 bottom-[88px] md:bottom-24 z-[9999] w-[calc(100vw-32px)] max-w-360 rounded-[16px] bg-white shadow-2xl border border-(--color-border) overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-8 px-16 py-10 bg-(--color-bold-heading) text-white">
        <span className="flex items-center gap-8 text-[14px] font-medium truncate">
          <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
          {heading}
        </span>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="p-2 rounded-full hover:bg-white/15 cursor-pointer"
          aria-label={collapsed ? "Expand uploads" : "Collapse uploads"}
        >
          {collapsed ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
        </button>
      </div>

      {!collapsed && (
        <ul className="max-h-240 overflow-y-auto divide-y divide-(--color-border)">
          {jobs.map((job) => {
            const running = job.status === "uploading" || job.status === "creating-episode";
            return (
              <li key={job.id} className="px-16 py-12">
                <div className="flex items-start gap-10">
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-medium text-(--color-bold-heading) truncate" title={job.fields.title}>
                      {job.fields.title}
                    </p>
                    <p
                      className={`text-[12px] mt-2 truncate ${
                        job.status === "error" ? "text-red-500" : "text-(--color-heading)"
                      }`}
                      title={statusLabel(job)}
                    >
                      {job.fileName} · {statusLabel(job)}
                    </p>
                  </div>
                  {job.status === "success" && <CheckCircleIcon className="text-green-500" sx={{ fontSize: 20 }} />}
                  {job.status === "error" && <ErrorOutlineIcon className="text-red-500" sx={{ fontSize: 20 }} />}
                  {job.status === "uploading" && (
                    <button
                      type="button"
                      onClick={() => setCancelJobId(job.id)}
                      className="p-2 rounded-full text-(--color-heading) hover:bg-[#F5F5F5] cursor-pointer"
                      aria-label="Cancel upload"
                      title="Cancel upload"
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </button>
                  )}
                  {!running && (
                    <button
                      type="button"
                      onClick={() => dismissPodcastUpload(job.id)}
                      className="p-2 rounded-full text-(--color-heading) hover:bg-[#F5F5F5] cursor-pointer"
                      aria-label="Dismiss"
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </button>
                  )}
                </div>

                {running && (
                  <div className="mt-8 h-4 rounded-full bg-[#EEE] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-(--color-primary) transition-[width] duration-300"
                      style={{ width: `${Math.max(job.progress, 2)}%` }}
                    />
                  </div>
                )}

                {(job.status === "error" || job.status === "cancelled") && (
                  <button
                    type="button"
                    onClick={() => retryPodcastUpload(job.id)}
                    className="mt-8 text-[13px] font-medium text-(--color-primary) hover:underline cursor-pointer"
                  >
                    Retry
                  </button>
                )}
                {job.status === "success" && job.episode && (
                  <Link
                    to={`/podcasts/${job.episode.userId}`}
                    className="mt-8 inline-block text-[13px] font-medium text-(--color-primary) hover:underline"
                  >
                    View episode
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {cancelJob && (
        <ConfirmModal
          title="Stop upload?"
          message={`"${cancelJob.fields.title || cancelJob.fileName}" is still uploading. If you stop now, this upload will be lost and you'll need to start over.`}
          confirmLabel="Stop upload"
          cancelLabel="Keep uploading"
          onConfirm={() => {
            cancelPodcastUpload(cancelJob.id);
            setCancelJobId(null);
          }}
          onCancel={() => setCancelJobId(null)}
        />
      )}
    </div>
  );
}
